import type { AppSettingsResponse, Match } from "@/lib/types";

type ClubLogoSettings = {
  settings?: Partial<Pick<AppSettingsResponse["settings"], "club_logo_filename">>;
  assets?: { club_logo_url?: AppSettingsResponse["assets"]["club_logo_url"] | null };
};

type ClubIconSettings = {
  settings?: Partial<Pick<AppSettingsResponse["settings"], "favicon_filename">>;
  assets?: { favicon_url?: AppSettingsResponse["assets"]["favicon_url"] | null };
};

/** A club's uploaded icon (including one derived from its uploaded logo),
 * never the bundled product icon returned when no usable file exists. */
export function getUploadedClubIconUrl(
  response: ClubIconSettings | null | undefined,
): string | null {
  const filename = response?.settings?.favicon_filename?.trim();
  const url = response?.assets?.favicon_url?.trim();
  if (!filename || !url) return null;
  try {
    const parsed = new URL(url, "https://tktic.invalid");
    if (parsed.pathname === "/static/logo.png" ||
        parsed.searchParams.get("v")?.startsWith("mark-")) return null;
  } catch {
    return null;
  }
  return url;
}

/** Match crests use uploaded club art only. The public logo URL can instead
 * point at a generated initials tile, and the favicon can be the app icon. */
export function getUploadedClubLogoUrl(
  response: ClubLogoSettings | null | undefined,
): string | null {
  const filename = response?.settings?.club_logo_filename?.trim();
  const url = response?.assets?.club_logo_url?.trim();
  if (!filename || !url) return null;

  // A stale filename can remain after its file disappears; the asset resolver
  // then returns the generated mark rather than an uploaded club image.
  try {
    const version = new URL(url, "https://tktic.invalid").searchParams.get("v");
    if (version?.startsWith("mark-")) return null;
  } catch {
    return null;
  }
  return url;
}

/** Resolved logo URLs for the two sides of a match. `null` means
 * "no logo available — fall back to the initial-letter chip". */
export type SideLogos = {
  homeUrl: string | null;
  awayUrl: string | null;
};

type LogoSourceMatch = Pick<Match, "id"> &
  Partial<
    Pick<
      Match,
      | "team_id"
      | "home_team"
      | "away_team"
      | "home_team_id"
      | "away_team_id"
      | "home_logo"
      | "away_logo"
      | "opponent_logo"
    >
  >;

/** Decide which side gets the club logo (always the tenant team) and
 * which side gets the opponent's per-match upload. Team pages may pass the
 * selected tenant-team name so legacy rows without structured side ids still
 * resolve the tenant side using the same name fallback as performance data.
 *
 * Single-club app today: the install carries one canonical
 * `club_logo_url` in settings. The tenant side of a match — whichever
 * side has `team_id === match.team_id` — always renders that. The
 * other side falls back to `match.opponent_logo` (the new per-match
 * upload) or the legacy `home_logo` / `away_logo` files for matches
 * that predate the migration. For "Not on this team" friendlies
 * where neither side is the tenant, the opponent slot is treated as
 * the away side (matches the legacy renderer's behavior) and the
 * home slot falls back to any legacy `home_logo`.
 *
 * Cache-busting is the caller's responsibility — append `?v=…` to
 * the returned URL when you've just uploaded a replacement and need
 * the browser to drop the old cached image.
 */
export function getSideLogos(
  match: LogoSourceMatch | null | undefined,
  clubLogoUrl: string | null | undefined,
  tenantTeamName?: string | null,
): SideLogos {
  if (!match) return { homeUrl: null, awayUrl: null };
  const tenantId = match.team_id ?? null;
  const structuredSide = Boolean(match.home_team_id || match.away_team_id);
  const normalizedTeamName = tenantTeamName?.trim().toLocaleLowerCase();
  const homeIsTenant = structuredSide
    ? !!tenantId && match.home_team_id === tenantId
    : Boolean(
        normalizedTeamName &&
          match.home_team?.trim().toLocaleLowerCase() === normalizedTeamName,
      );
  const awayIsTenant = structuredSide
    ? !!tenantId && match.away_team_id === tenantId
    : Boolean(
        normalizedTeamName &&
          match.away_team?.trim().toLocaleLowerCase() === normalizedTeamName,
      );
  const opponentEndpoint = match.opponent_logo
    ? `/api/matches/${match.id}/opponent-logo`
    : null;
  const legacyHome = match.home_logo
    ? `/api/matches/${match.id}/logo/home`
    : null;
  const legacyAway = match.away_logo
    ? `/api/matches/${match.id}/logo/away`
    : null;

  // Tenant side → club logo. Other side → opponent (with legacy fallback).
  if (homeIsTenant) {
    return {
      homeUrl: clubLogoUrl || null,
      awayUrl: opponentEndpoint ?? legacyAway,
    };
  }
  if (awayIsTenant) {
    return {
      homeUrl: opponentEndpoint ?? legacyHome,
      awayUrl: clubLogoUrl || null,
    };
  }
  // Friendly with no tenant team — fall back to legacy per-side files,
  // letting opponent_logo populate the away slot if it's the only thing
  // we have.
  return {
    homeUrl: legacyHome,
    awayUrl: opponentEndpoint ?? legacyAway,
  };
}

/** Returns true when, for this match, the operator's per-match upload
 * controls the HOME side (i.e. the tenant team is the AWAY side). Used
 * by MatchEditModal to label the existing-logo preview correctly. */
export function opponentIsHomeSide(match: LogoSourceMatch | null | undefined): boolean {
  if (!match) return false;
  const tenantId = match.team_id ?? null;
  return !!tenantId && match.away_team_id === tenantId;
}

/** The editor resolves the saved opponent side with its name fallback.
 * A per-match upload always wins, including partially migrated side IDs. */
export function getOpponentLogoUrl(
  match: LogoSourceMatch | null | undefined,
  opponentSide: "home" | "away",
): string | null {
  if (!match) return null;
  const base = `/api/matches/${encodeURIComponent(match.id)}`;
  if (match.opponent_logo) return `${base}/opponent-logo`;
  return match[`${opponentSide}_logo`] ? `${base}/logo/${opponentSide}` : null;
}
